<template>
  <div class="c-product-info__drawer l-container">
    <div class="c-product-info__drawer__selectedItems">
      <div class="c-product-info__drawer__selectedItems-heading">
        <button
          v-if="!miniBundle"
          class="c-product-info__drawer__exit t-heading-6 u-margin-bottom-xx-small"
          @click="closeModal"
        >
          <span aria-hidden="true">&lt; </span>
          <span class="u-all-caps">Exit</span>
        </button>
        <h1
          class="c-product-info__drawer__title t-heading-6 u-margin-bottom-xx-small"
        >
          {{ title }}
        </h1>
        <div class="c-product-info__drawer__price">
          <h1
            class="t-heading-8 u-margin-bottom-xx-small"
          >
            ${{ price }}
          </h1>
          <p
            v-if="originalPrice"
            class="t-heading-8 t-heading-8--light u-strikeout"
            :aria-label="`original price: $${originalPrice}.`"
          >
            <span>{{ originalPrice | currency }}</span>
          </p>
        </div>
        <nuxt-img
          v-if="miniBundle"
          class="c-product-info__drawer__mini-bundle-img"
          :src="product.featuredImage.src"
          :alt="product.featuredImage.alt"
          width="420"
          provider="shopify"
        />
      </div>
      <div class="c-product-info__drawer__selectedItems-content">
        <product-info-multi-active
          :active-options="activeOptions"
          :active-bundle-type-product="activeBundleTypeProduct"
          :active-bundle-type-size="activeBundleTypeSize"
          :size-options="sizeOptions"
          :is-mobile="isMobile"
          :single-size="singleSize"
          :bundle-slot-count="bundleSlotCount"
          :active-selections="activeSelections"
          :grid-cols="gridCols"
          @update:size="$emit('update:size', $event)"
          @update:bundleProduct="handleBundleProduct"
          @update:selection="$emit('update:selection', $event)"
        />
      </div>
    </div>
    <div class="c-product-info__drawer__select">
      <slot name="filterBar" />
      <div
        class="c-product-info__drawer__select__heading"
      >
        <button
          v-if="bundleCardOpen"
          class="c-product-info__drawer__select__reset-button"
          @click="resetSelectStep"
        >
          <span aria-hidden="true">&lt; </span><span>Back</span>
        </button>
        <h3
          v-else
          class="t-heading-6"
        >
          Select ring ({{ bundleSlotIndex + 1 }} / {{ bundleSlotCount }}):
        </h3>
      </div>
      <slot name="close" />
      <product-grid
        :columns="gridCols"
        :products="products"
        :bundle="product"
        :bundle-product="bundleProduct"
        :filtered="filtered"
        :active-options="activeOptions"
        :max-products="25"
        :compact="true"
        :irregular-prices="irregularPrices"
        :bundle-slot-count="bundleSlotCount"
        @update:selection="addToBundle"
        @update:bundleProduct="handleBundleProduct"
        @modal:open="$emit('modal:open')"
        @engraving="$emit('engraving', $event)"
      />
    </div>
  </div>
</template>

<script>
import get from "lodash/get";
import ProductInfoMultiActive from "./ProductInfoMultiActive";
import ProductGrid from "../productGrid/ProductGrid";
import BundleStateMixin from "./BundleStateMixin";
import GlobalDrawerRefMixin from './GlobalDrawerRefMixin'

export default {
  components: {
    ProductInfoMultiActive,
    ProductGrid
  },
  mixins: [
    BundleStateMixin,
    GlobalDrawerRefMixin,
  ],
  props: {
    product: {
      type: Object,
      default: () => ({})
    },
    size: {
      type: [Number, String],
      default: null
    },
    isGiftCard: {
      type: Boolean,
      default: false
    },
    products: {
      type: Array,
      default: () => []
    },
    filtered: {
      type: Boolean,
      default: false
    },
    gridCols: {
      type: Number,
      default: 3
    },
    miniBundle: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    title() {
      return get(this.product, "title", null);
    },
    price() {
      const product = this.product;
      let result = 0;
      result = get(product, "variants[0].price");
      return result ? result : 0;
    },
    bundleSlotIndex() {
      const activeSlots = this.activeBundleTypeProduct;
      let selections = [];
      for (let [bundleType, product] of Object.entries(activeSlots)) {
        if (product && product.variant) {
          selections.push(`${bundleType}-${product.handle}+variants`);
        } else {
          selections.push(null);
        }
      }
      return selections.indexOf(null);
    },
    bundleCardOpen() {
      return !!this.bundleProduct;
    },
    originalPrice() {
      let result;
      const product = this.product;
      if(product) {
        result = get(product, 'variants[0].compareAtPrice');
      }
      return (result === '0.00') ? undefined : result;
    },
    calculatedHeight() {
      const selectElement = document.querySelector('.c-product-info__drawer__select');
      const filterElement = document.querySelector('.c-filter-bar');
      const totalHeight = selectElement.offsetHeight + (filterElement ? filterElement.offsetHeight : 0);
      return `${totalHeight}px`;
    }
  },
  methods: {
    closeModal() {
      this.$emit("close");
      this.$emit("update:bundleProduct", null);
    },
  }
};
</script>

<style lang="scss" scoped>
.u-all-caps {
  text-transform: uppercase;
}
.c-product-info__drawer {
  width: 100vw;
  height: auto;
  &__mini-bundle-img {
    max-width: calc(100% - 3rem);
  }
  &__exit {
    @include buttonReset;
    cursor: pointer;
    display: block;
    padding: rem(15px) 0 rem(25px) 0;
  }
  &__selectedItems {
    width: 30%;
    height: v-bind('calculatedHeight');
    float: left;
    @include themify($themes) {
      background-color: themed("background", "xx-light");
    }
    &-heading {
      width: 100%;
      padding-left: rem(30px);
      margin-top: rem(20px);
      @include themify($themes) {
        border-bottom: $border themed("foreground", "select");
      }
    }
  }
  &__select {
    position: relative;
    width: 70%;
    height: auto;
    float: right;
    &__heading {
      margin-top: rem(30px);
      margin-left: rem(20px);
      display: flex;
    }
    &__reset-button {
      @include buttonReset;
      cursor: pointer;
    }
    ::v-deep {
      .c-product-grid {
        padding-bottom: 0;
      }
    }
  }
  &__price {
    h1 {
      display: inline;
    }
    p {
      display: inline;
    }
  }
}

.l-container {
  margin: 0 auto;
}
</style>
